import { z } from 'zod';
import { HerkunftFelder } from './auftrag-zusammenfuehren.js';

/**
 * PV-052 L3: gebuchte Stunden einer Besetzung aus `besetzung_ist` (SF-Zeiterfassung, Stunden).
 * Bewusst `z.number()` ohne Untergrenze: Korrekturbuchungen können eine Summe negativ machen, und
 * ein Parse-Fehler ließe die ganze Besetzungssicht mit 500 scheitern.
 */
export const BesetzungGebuchtSchema = z.object({
  stunden_vorort: z.number(),
  stunden_remote: z.number(),
  letzte_buchung: z.string().date().nullable(),
});
export type BesetzungGebucht = z.infer<typeof BesetzungGebuchtSchema>;

/**
 * Ist-Seite (PV-032): eine Projektbesetzung aus Salesforce, wie der Server sie
 * ausliefert. Ausschliesslich sync-gepflegt — es gibt kein Schreib-Schema dazu.
 * Geldbetraege kommen als `numeric` aus Postgres und damit als String; sie
 * werden an der Boundary in Number gewandelt.
 */
export const ProjektBesetzungSchema = z.object({
  projekt_besetzung_id: z.string().uuid(),
  auftrag_id: z.string().uuid(),
  /** Intern aus Mitarbeiter (User), extern aus Externe Projektbesetzung (Contact). */
  person_name: z.string(),
  ist_extern: z.boolean(),
  ek: z.number().nullable(),
  ek_freiberufler: z.number().nullable(),
  ek_freiberufler_remote: z.number().nullable(),
  ek_freiberufler_nkp: z.number().nullable(),
  vk_stundensatz: z.number().nullable(),
  vk_stundensatz_remote: z.number().nullable(),
  vk_nebenkostenpauschale: z.number().nullable(),
  /** Kontext-Anzeige. Fliesst NICHT in eine Bewertung ein (Nicht-Ziel). */
  beauftragte_pt: z.number().nullable(),
  verbleibende_pt: z.number().nullable(),
  synced_at: z.string(),
  /** PV-052 L3: Ist per Join über die SF-Id; `null` = (noch) kein Ist. Optional für Altfixtures. */
  gebucht: BesetzungGebuchtSchema.nullable().optional(),
});
export type ProjektBesetzung = z.infer<typeof ProjektBesetzungSchema>;

/**
 * PV-052 L3: in SF nicht mehr aktive Besetzung mit ihren Buchungen (nur Anzeige).
 */
export const EhemaligeBesetzungSchema = z.object({
  sf_besetzung_id: z.string(),
  person_name: z.string(),
  ist_extern: z.boolean(),
  gebucht: BesetzungGebuchtSchema,
});
export type EhemaligeBesetzung = z.infer<typeof EhemaligeBesetzungSchema>;

export const PreisEinheitSchema = z.enum(['stunde', 'tag']);
export type PreisEinheit = z.infer<typeof PreisEinheitSchema>;

export const BeauftragtePositionStatusSchema = z.enum(['ki_vorschlag', 'bestaetigt']);
export type BeauftragtePositionStatus = z.infer<typeof BeauftragtePositionStatusSchema>;

/**
 * Soll-Seite (PV-033): eine bestellte STUFE ("Stufe X zu Y EUR/h fuer Z Tage").
 * Bewusst ohne Personenanzahl — das entspricht der Bestellrealitaet.
 */
export const BeauftragtePositionSchema = z
  .object({
    beauftragte_position_id: z.string().uuid(),
    project_id: z.string().uuid(),
    stufe: z.string(),
    preis: z.number().nullable(),
    preis_einheit: PreisEinheitSchema,
    anzahl_pt: z.number().nullable(),
    status: BeauftragtePositionStatusSchema,
    unsicher: z.boolean(),
    /** #158: das eine Notizfeld je Position. */
    notiz: z.string().nullable().optional(),
    created_by: z.string().uuid().nullable(),
    confirmed_by: z.string().uuid().nullable(),
    created_at: z.string(),
  })
  .extend(HerkunftFelder);
export type BeauftragtePosition = z.infer<typeof BeauftragtePositionSchema>;

const stufeField = z.string().trim().min(1, 'stufe_required').max(200);
const preisField = z.number().nonnegative().max(100000);
const ptField = z.number().nonnegative().max(100000);

/** Manuelles Anlegen ueber den "+"-Button. Ergebnis ist immer `bestaetigt`. */
export const CreateBeauftragtePositionSchema = z.object({
  stufe: stufeField,
  preis: preisField.optional(),
  preis_einheit: PreisEinheitSchema.default('stunde'),
  anzahl_pt: ptField.optional(),
  /** #158 F4: Notiz gleich beim Anlegen (Formular „Position hinzufügen"); leer = keine. */
  notiz: z.string().trim().max(5000).nullish(),
});
export type CreateBeauftragtePosition = z.infer<typeof CreateBeauftragtePositionSchema>;

/**
 * Aendern einer Zeile — gilt fuer BEIDE Status gleichermassen (Spec: bestaetigte
 * Zeilen sind genauso aenderbar wie Vorschlaege). Leeres Objekt ist ungueltig,
 * damit ein versehentlicher Leer-PATCH nicht als Erfolg durchgeht.
 */
export const PatchBeauftragtePositionSchema = z
  .object({
    stufe: stufeField.optional(),
    preis: preisField.nullable().optional(),
    preis_einheit: PreisEinheitSchema.optional(),
    anzahl_pt: ptField.nullable().optional(),
    status: BeauftragtePositionStatusSchema.optional(),
    unsicher: z.boolean().optional(),
    /** #158: das eine Notizfeld je Position; leer/`null` löscht sie. */
    notiz: z.string().trim().max(5000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'empty_patch' });
export type PatchBeauftragtePosition = z.infer<typeof PatchBeauftragtePositionSchema>;

/**
 * Output-Schema des LLM (Layer-1-Guardrail). Bewusst tolerant bei Preis/PT:
 * fehlende Angaben sind erlaubt, geraten wird nicht — dafuer gibt es `unsicher`.
 */
export const ExtractedPositionSchema = z.object({
  stufe: stufeField,
  preis: preisField.optional(),
  preis_einheit: PreisEinheitSchema.optional(),
  anzahl_pt: ptField.optional(),
  unsicher: z.boolean().default(false),
});
export type ExtractedPosition = z.infer<typeof ExtractedPositionSchema>;

export const ExtractedPositionListSchema = z.array(ExtractedPositionSchema).max(50);

/** Eine Zeile der zusammengefuehrten Abgleichsliste (Option B aus dem Wireframe). */
export const AbgleichZeileSchema = z.object({
  kind: z.enum(['besetzt', 'offen', 'nicht_zugeordnet']),
  position: BeauftragtePositionSchema.nullable(),
  besetzungen: z.array(ProjektBesetzungSchema),
});
export type AbgleichZeile = z.infer<typeof AbgleichZeileSchema>;

/** Antwort von GET /v1/besetzung/projects/:id */
export const BesetzungViewSchema = z.object({
  ist: z.array(ProjektBesetzungSchema),
  /** PV-052 L3: nicht mehr aktive Besetzungen aus `besetzung_ist` (stehen nicht in `ist`). */
  ehemalige: z.array(EhemaligeBesetzungSchema).optional(),
  soll: z.array(BeauftragtePositionSchema),
  abgleich: z.array(AbgleichZeileSchema),
  /** true, wenn sich die Projektnotiz seit der letzten Extraktion geaendert hat. */
  notiz_veraltet: z.boolean(),
  /** false, wenn der fuehrende Auftrag keinen Notiztext hat (Button deaktiviert). */
  notiz_vorhanden: z.boolean(),
  /**
   * Die Annahme "1 Personentag = N Stunden" aus `config/agent.yaml`. Wandert
   * bewusst in die Antwort: die Spec verlangt, dass sie im Tooltip SICHTBAR ist —
   * eine im Frontend hartkodierte 8 wuerde bei geaenderter Konfiguration luegen.
   */
  pt_stunden: z.number().positive(),
});
export type BesetzungView = z.infer<typeof BesetzungViewSchema>;
